import { useState } from "react";
import { Requirement, Select } from "../../types/requirement.response";
import { AddableCourse, CourseLite, CourseOption, CourseSummary } from "../../types/course.response";
import { ProgramInfo } from "../../types/program.response";
import { CourseRequirementOverride } from "../../services/api/user-courses.api";
import { getBroadKind } from "../../utils/requirementResponseUtils";
import { getRequirementPanels } from "./panelUtils";
import { useRequirementNav } from "./useRequirementNav";
import { useForceApplyAdd } from "./useForceApplyAdd";
import OptionPills from "./OptionPills";
import PanelView from "./PanelView";

interface RequirementBodyProps {
    programInfo: ProgramInfo;
    requirement: Requirement;
    coursesMap: Record<string, CourseSummary>;
    handleAddCourse: (newCourse: AddableCourse, overrides: CourseRequirementOverride[], semester: string | null, classSectionIds?: number[]) => void;
    handleDeleteCourse: (deletedCourse: CourseLite) => void;
    handleFavoriteCourse: (courseOption: CourseLite) => void;
    handleOverride: (course: CourseLite, override: CourseRequirementOverride) => void | Promise<void>;
    optimisticFavorites: Map<string, boolean>;
    userCourses?: CourseOption[];
    onRequirementClick?: (programId: string, requirementId: string) => void;
}

/**
 * 统一的 requirement 内容区:LIST/GROUP/OPTION/OPTION_GROUP 共用同一套渲染。
 * 是否有 pills、课程怎么算都由 getRequirementPanels / buildPanelRows 决定(见 panelUtils)。
 */
export default function RequirementBody({
    programInfo,
    requirement,
    coursesMap,
    handleAddCourse,
    handleDeleteCourse,
    handleFavoriteCourse,
    handleOverride,
    optimisticFavorites,
    userCourses,
    onRequirementClick,
}: RequirementBodyProps) {
    const { tabbed, panels } = getRequirementPanels(requirement);
    const [activeIndex, setActiveIndex] = useState(0);
    const navigateToRequirement = useRequirementNav(programInfo, requirement, onRequirementClick);
    const onOptionAdd = useForceApplyAdd(requirement, handleAddCourse, handleOverride);

    if (panels.length === 0) return null;

    const activeIndexClamped = Math.min(activeIndex, panels.length - 1);
    const activeNode = panels[activeIndexClamped];
    const fulfilledChildIds = tabbed ? (requirement.rootNode as Select).fulfilledChildIds : [];
    const nodeFulfilled = tabbed && new Set(fulfilledChildIds).has(activeNode.id);

    return (
        <>
            {tabbed && (
                <OptionPills
                    panels={panels}
                    activeIndex={activeIndexClamped}
                    fulfilledChildIds={fulfilledChildIds}
                    onSelect={setActiveIndex}
                />
            )}
            <PanelView
                key={activeNode.id}
                programId={programInfo.id}
                requirement={requirement}
                node={activeNode}
                coursesMap={coursesMap}
                relevantSubjects={programInfo.relevantSubjects}
                showProgress={tabbed}
                nodeFulfilled={nodeFulfilled}
                introHeuristic={!tabbed}
                broadKind={getBroadKind(requirement)}
                handleAddCourse={handleAddCourse}
                handleDeleteCourse={handleDeleteCourse}
                handleFavoriteCourse={handleFavoriteCourse}
                handleOverride={handleOverride}
                optimisticFavorites={optimisticFavorites}
                userCourses={userCourses}
                onSeeAll={navigateToRequirement}
                onOptionAdd={onOptionAdd}
            />
        </>
    );
}
